import {
  freetextContainsBadWord,
  fullNameContainsBadWord,
  hasEmojis,
  UserFacingError,
  usernameOrTagContainsBadWord,
  validateStringLength
} from '.'
import {MAX_BIO_LENGTH, MAX_SCREENNAME_LENGTH, MIN_SCREENNAME_LENGTH} from './GalleryConstants'

export const illegalScreennameCharacterCombos = ['__', '--', '-_', '_-']
export const validScreennameCharacters = /^[a-z0-9_-]+$/
export const validScreennameStartingCharacter = /^[a-z]/
export const validScreennameTrailingCharacter = /[a-z0-9]$/
export const invalidScreennameTrailingCharacters = /[^a-zA-Z0-9]+$/
export const invalidScreennameLeadingCharacters = /^[^a-zA-Z0-9@]+/
export const screennameMentionRegex = /(?:\B@(?<screenname>(?:\w|-)+)\b)/g
// Regex to support various names including ones with apostrophes, hyphens, and other languages
// Does not allow for numbers or other punctuation (Smith & Jones)
export const validNameCharacters = /^[\p{L}\p{M}]+([\s'-][\p{L}\p{M}]+)*(\s+[IVXLCDM]+)?$/u

export function getScreennameMentionsFromText(text: string): string[] {
  const matches = text.match(screennameMentionRegex)
  if (!matches) {
    return []
  }

  return matches
}

export function validateFullName(fullName?: string) {
  if (fullName === undefined || fullName === null) {
    return
  }

  if (fullName === '') {
    throw new UserFacingError('nameFormatRequirementError')
  }

  if (fullNameContainsBadWord(fullName)) {
    throw new UserFacingError('nameNotAvailableError')
  }

  if (hasEmojis(fullName)) {
    throw new UserFacingError('nameFormatRequirementError')
  }

  if (!fullName.match(validNameCharacters)) {
    throw new UserFacingError('nameFormatRequirementError')
  }
}

/**
 * Evaluate the checks that make sense on an in-progress screenname (character set,
 * case, illegal combos). Safe to call on partial input from an onChange handler —
 * an empty string is accepted so deletion-to-empty doesn't error.
 *
 * @param screenname The (possibly partial) screenname to evaluate.
 */
export function validatePartialScreenname(screenname: string) {
  if (screenname === '') {
    return
  }

  if (screenname !== screenname.toLowerCase()) {
    throw new UserFacingError('screennameLowercaseRequirementError')
  }

  if (
    !screenname.match(validScreennameCharacters) ||
    illegalScreennameCharacterCombos.some((illegalCombo) => screenname.includes(illegalCombo))
  ) {
    throw new UserFacingError('screennameFormatError')
  }
}

/**
 * Evaluate the provided screenname and throw user-friendly error string if there
 * is something off about it.
 *
 * @param screenname The screenname to be evaluated for use.
 */
export function validateScreenname(screenname: string) {
  if (screenname.length > MAX_SCREENNAME_LENGTH) {
    throw new UserFacingError({key: 'screennameTooLongError', args: {maxScreennameLength: `${MAX_SCREENNAME_LENGTH}`}})
  }
  if (screenname.length < MIN_SCREENNAME_LENGTH) {
    throw new UserFacingError({key: 'screennameTooShortError', args: {minScreennameLength: `${MIN_SCREENNAME_LENGTH}`}})
  }

  if (usernameOrTagContainsBadWord(screenname)) {
    throw new UserFacingError('screennameNotAvailableError')
  }

  if (screenname.toLowerCase().includes('notability')) {
    throw new UserFacingError('screennameNotAvailableError')
  }

  validatePartialScreenname(screenname)

  if (!screenname.match(validScreennameStartingCharacter)) {
    throw new UserFacingError('screennameAlphaStart')
  }

  if (!screenname.match(validScreennameTrailingCharacter)) {
    throw new UserFacingError('screennameAlphanumericEnd')
  }
}

/**
 * Evaluate the provided bio and throw user-friendly error string if there
 * is something off about it.
 *
 * @param bio The bio blurb to be evaluated for use.
 */
export function validateBio(bio: string) {
  validateStringLength(bio, {
    max: {
      length: MAX_BIO_LENGTH,
      error: {key: 'bioTooLongError', args: {maxLength: `${MAX_BIO_LENGTH}`}}
    }
  })

  if (freetextContainsBadWord(bio)) {
    throw new UserFacingError('textContainsProfanity')
  }
}

//https://help.etsy.com/hc/en-us/articles/115015672808-How-to-Open-an-Etsy-Shop?segment=selling
export const etsyUsernameMaxLength = 20
export const etsyUsernameMinLength = 4

export function sanitizeEtsyUsername(username?: string) {
  if (!username) {
    return username
  }
  validateStringLength(username, {
    max: {
      length: etsyUsernameMaxLength,
      error: {
        key: 'etsyHandleLengthError',
        args: {minLength: `${etsyUsernameMinLength}`, maxLength: `${etsyUsernameMaxLength}`}
      }
    },
    min: {
      length: etsyUsernameMinLength,
      error: {
        key: 'etsyHandleLengthError',
        args: {minLength: `${etsyUsernameMinLength}`, maxLength: `${etsyUsernameMaxLength}`}
      }
    }
  })

  return removeAtPrefix(username)
}

// https://help.twitter.com/en/managing-your-account/change-twitter-handle
// Note: legacy twitter handles can be minimum of length 1. sometimes new accounts with <4 characters like @AOC get added
export const twitterUsernameMaxLength = 15
export const twitterUsernameMinLength = 1

export function sanitizeTwitterUsername(username?: string) {
  if (!username) {
    return username
  }
  validateStringLength(username, {
    max: {
      length: twitterUsernameMaxLength,
      error: {
        key: 'twitterHandleLengthError',
        args: {minLength: `${twitterUsernameMinLength}`, maxLength: `${twitterUsernameMaxLength}`}
      }
    },
    min: {
      length: twitterUsernameMinLength,
      error: {
        key: 'twitterHandleLengthError',
        args: {minLength: `${twitterUsernameMinLength}`, maxLength: `${twitterUsernameMaxLength}`}
      }
    }
  })

  return removeAtPrefix(username)
}

// https://www.followchain.org/tiktok-handle/
// Note: could not find this directly from tiktok pages
export const tikTokUsernameMaxLength = 24
export const tikTokUsernameMinLength = 2
export function sanitizeTikTokUsername(username?: string) {
  if (!username) {
    return username
  }
  validateStringLength(username, {
    max: {
      length: tikTokUsernameMaxLength,
      error: {
        key: 'tikTokHandleLengthError',
        args: {minLength: `${tikTokUsernameMinLength}`, maxLength: `${tikTokUsernameMaxLength}`}
      }
    },
    min: {
      length: tikTokUsernameMinLength,
      error: {
        key: 'tikTokHandleLengthError',
        args: {minLength: `${tikTokUsernameMinLength}`, maxLength: `${tikTokUsernameMaxLength}`}
      }
    }
  })

  return removeAtPrefix(username)
}

// https://stackoverflow.com/questions/15470180/character-limit-on-instagram-usernames
// Note: could not find on instagram support
export const instagramUsernameMaxLength = 30
export const instagramUsernameMinLength = 1
export function sanitizeInstagramUsername(username?: string) {
  if (!username) {
    return username
  }
  validateStringLength(username, {
    max: {
      length: instagramUsernameMaxLength,
      error: {
        key: 'instagramHandleLengthError',
        args: {minLength: `${instagramUsernameMinLength}`, maxLength: `${instagramUsernameMaxLength}`}
      }
    },
    min: {
      length: instagramUsernameMinLength,
      error: {
        key: 'instagramHandleLengthError',
        args: {minLength: `${instagramUsernameMinLength}`, maxLength: `${instagramUsernameMaxLength}`}
      }
    }
  })

  return removeAtPrefix(username)
}

// Setting a reasonably large value for this.
// Standard form is https://youtube.com/channel/UCUZHFZ9jIKrLroW8LcyJEQQ
// Custom form is https://youtube.com/c/MyCustomURL
// Ref https://support.google.com/youtube/answer/2657968?hl=en&co=GENIE.Platform%3DDesktop
export const youtubeUrlMaxLength = 1024
export function sanitizeYoutubeUrl(url?: string) {
  if (!url) {
    return url
  }

  // optionally matches 'http/https' and 'www'. requires youtube.com and one of c, channel, or user
  const validYoutubePrefixes: RegExp = /^((http(s?))\:\/\/)?(www\.)?(youtube\.com\/)/i

  if (!url.match(validYoutubePrefixes)) {
    throw new UserFacingError('youtubeUrlPrefixError')
  }

  validateStringLength(url, {
    max: {
      length: youtubeUrlMaxLength,
      error: {key: 'youtubeUrlHandleLengthError', args: {maxLength: `${youtubeUrlMaxLength}`}}
    }
  })

  return url.replace(validYoutubePrefixes, 'https://www.youtube.com/')
}

function removeAtPrefix(s: string) {
  if (s.startsWith('@')) {
    return s.slice(1)
  }

  return s
}
